/**
 * 阿里云验证码 2.0 Web / H5 客户端 V3 架构接入辅助（与站点 site/utils/aliyunCaptcha.js 保持一致）
 *
 * @see https://help.aliyun.com/zh/captcha/captcha2-0/user-guide/new-architecture-for-web-and-h5-client-access
 */

const MIN_MS_BEFORE_VERIFY_REQUEST = 2000;

let scriptInjectPromise = null;

export function serializeCaptchaVerifyParam(raw) {
  if (raw == null || raw === "") {
    return "";
  }
  if (typeof raw === "string") {
    return raw.trim();
  }
  try {
    return JSON.stringify(raw);
  } catch (e) {
    return "";
  }
}

export function setAliyunCaptchaGlobalConfig(region, prefix) {
  if (typeof window === "undefined") {
    return;
  }
  window.AliyunCaptchaConfig = {
    region: region === "sgp" ? "sgp" : "cn",
    prefix: prefix || "",
  };
}

export function loadAliyunCaptchaMainScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (typeof window.initAliyunCaptcha === "function") {
    if (window.__bbsGoAliyunCaptchaScriptLoadedAt == null) {
      window.__bbsGoAliyunCaptchaScriptLoadedAt = Date.now();
    }
    return Promise.resolve();
  }
  if (scriptInjectPromise) {
    return scriptInjectPromise;
  }
  scriptInjectPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js";
    s.async = true;
    s.onload = () => {
      window.__bbsGoAliyunCaptchaScriptLoadedAt = Date.now();
      resolve();
    };
    s.onerror = () => {
      scriptInjectPromise = null;
      reject(new Error("阿里云验证码脚本加载失败"));
    };
    document.head.appendChild(s);
  });
  return scriptInjectPromise;
}

export function prepareAliyunCaptcha(region, prefix) {
  setAliyunCaptchaGlobalConfig(region, prefix);
  return loadAliyunCaptchaMainScript();
}

export function ensureAliyunCaptchaVerifyDelay() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  const now = Date.now();
  const loadT = window.__bbsGoAliyunCaptchaScriptLoadedAt;
  const initT = window.__bbsGoAliyunCaptchaInitedAt;
  let need = 0;
  if (typeof loadT === "number") {
    need = Math.max(need, MIN_MS_BEFORE_VERIFY_REQUEST - (now - loadT));
  }
  if (typeof initT === "number") {
    need = Math.max(need, MIN_MS_BEFORE_VERIFY_REQUEST - (now - initT));
  }
  if (loadT == null && initT == null) {
    need = MIN_MS_BEFORE_VERIFY_REQUEST;
  }
  if (need <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => setTimeout(resolve, need));
}

export function bindAliyunCaptchaWidgetWhenReady(opts) {
  if (typeof window === "undefined") {
    return () => {};
  }
  let rafId = 0;
  let cancelled = false;
  const maxAttempts = opts.maxAttempts ?? 120;
  let attempts = 0;

  function cancel() {
    cancelled = true;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  }

  function tick() {
    if (cancelled) {
      return;
    }
    if (opts.shouldKeepTrying && !opts.shouldKeepTrying()) {
      if (opts.onAbort) {
        opts.onAbort();
      }
      return;
    }
    attempts++;
    if (attempts > maxAttempts) {
      if (opts.onGiveUp) {
        opts.onGiveUp();
      }
      return;
    }
    if (typeof window.initAliyunCaptcha !== "function") {
      rafId = requestAnimationFrame(tick);
      return;
    }
    const btn = opts.buttonSelector && document.querySelector(opts.buttonSelector);
    const el = opts.elementSelector && document.querySelector(opts.elementSelector);
    if (!btn || !el) {
      rafId = requestAnimationFrame(tick);
      return;
    }
    initAliyunCaptchaWidget({
      sceneId: opts.sceneId,
      elementSelector: opts.elementSelector,
      buttonSelector: opts.buttonSelector,
      onSuccess: opts.onSuccess,
      onFail: opts.onFail,
      getInstance: opts.getInstance,
    });
    if (opts.onBound) {
      opts.onBound();
    }
  }

  rafId = requestAnimationFrame(tick);
  return cancel;
}

export function initAliyunCaptchaWidget(opts) {
  if (typeof window === "undefined" || typeof window.initAliyunCaptcha !== "function") {
    return;
  }
  window.initAliyunCaptcha({
    SceneId: opts.sceneId,
    mode: "popup",
    element: opts.elementSelector,
    button: opts.buttonSelector,
    delayBeforeSuccess: false,
    language: "cn",
    slideStyle: { width: 360, height: 40 },
    success(captchaVerifyParam) {
      if (opts.onSuccess) {
        opts.onSuccess(serializeCaptchaVerifyParam(captchaVerifyParam));
      }
    },
    fail(info) {
      if (opts.onFail) {
        opts.onFail(info);
      }
    },
    getInstance(instance) {
      window.__bbsGoAliyunCaptchaInitedAt = Date.now();
      if (opts.getInstance) {
        opts.getInstance(instance);
      }
    },
  });
}
